import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    financeCase: String,
    creditAmountInvestment: Number,
    durationInvestment: Number,
    olderThanFiveYears: Boolean,
    practiceType: String,
    medicalSpecialisation: String,
    isInstantLoan: Boolean,
    brokerRate: Number,
    brokerName: String,
  }

  static targets = [
    "instantLoanInfo",
    "financeCasePick",
    "deviceFinancingType",
    "creditAmountInvestment",
    "durationInvestment",
    "interestRateOutput",
    "interestRateOutputOnlyNumber",
    "installmentOutput",
    "installmentOutputOnlyNumber",
    "kfwLogo",
    "vvrbLogo",
    "disclaimer",
    "financeCaseOutput",
    "amountOutput",
    "durationOutput",
    "practiceTypeOutput",
    "medicalSpecialisationOutput",
    "hiddenInterestRateOutputOnlyNumber",
    "brokerNameOutput"
  ];

  olderThanFiveYears = false;
  normalizedBrokerRate = 0;

  connect() {
    // Parse the tableaus from the data attribute
    this.normalizedBrokerRate = Math.max(0, this.brokerRateValue - 50);
    this.allTableaus = JSON.parse(this.element.dataset.interestRateTableaus);
    this.olderThanFiveYears = this.olderThanFiveYearsValue;
    this.calculateInterestRate();

    this.brokerNameOutputTargets.forEach(target => {
      if (this.brokerNameValue != null && this.brokerNameValue != "") {
        target.innerText = 'Anfrage des Praxis-DirektKredit durch: ' + this.brokerNameValue;
      }
    });
  }

  calculateInterestRate() {
    // Retrieve values from the form
    const financeCase = this.hasFinanceCasePickTarget ? this.financeCasePickTarget.value : this.financeCaseValue;
    const deviceFinancingType = this.hasDeviceFinancingTypeTarget ? this.deviceFinancingTypeTarget.value : this.deviceFinancingTypeValue;
    const amount = this.hasCreditAmountInvestmentTarget ? parseInt(this.creditAmountInvestmentTarget.value) : parseInt(this.creditAmountInvestmentValue);
    const duration = this.hasDurationInvestmentTarget ? parseInt(this.durationInvestmentTarget.value) : parseInt(this.durationInvestmentValue);
    // Determine the rating class
    const ratingClass = financeCase === "New Practice" ? "B" : "A";

    // Determine if KFW applies
    const kfw = ["New Practice", "Buy Practice", "Existing Practice"].includes(financeCase);
    const instantLoanableFinanceCase = ['Equipment', 'Liquidity', 'Debt Restructuring'].includes(financeCase) && (deviceFinancingType === 'Loan' || deviceFinancingType === "Dont Know" || deviceFinancingType === null || deviceFinancingType === '' || typeof deviceFinancingType === 'undefined');
    const instantLoan = this.isInstantLoanValue || instantLoanableFinanceCase && amount > 0 && amount <= 100000 && duration > 0 && duration <= 60;

    // Filter possible tableaus
    const possibleTableaus = this.possibleProductTableaus(amount, duration, ratingClass, kfw, instantLoan);
    if (possibleTableaus.length > 0) {
      // Find the cheapest tableau
      const tableau = this.cheapestProduct(possibleTableaus);

      // Calculate monthly installment and interest rate
      const monthlyInstallment = this.calculateMonthlyInstallment(tableau, amount, duration);
      const ageModifier = this.olderThanFiveYears ? 0.2 : 0
      const brokerModifier = Math.ceil((this.normalizedBrokerRate)/ (duration / 12 / 2) / 100 * 20) / 20

      // Update the output in the DOM
      this.interestRateOutputTargets.forEach(target => target.innerHTML = `ab <strong>${(tableau.InterestRate__c + ageModifier + brokerModifier).toLocaleString('de-DE')}%</strong> p.a.&#185;`);
      this.interestRateOutputOnlyNumberTargets.forEach(target => target.innerHTML = `${(tableau.InterestRate__c + ageModifier + brokerModifier).toLocaleString('de-DE')}%`);
      this.installmentOutputTargets.forEach(target => target.innerHTML = `ab <strong>${monthlyInstallment.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</strong> mtl.`);
      this.installmentOutputOnlyNumberTargets.forEach(target => target.innerHTML = `${monthlyInstallment.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`);
      this.amountOutputTargets.forEach(target => target.innerHTML = `${amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}`);
      this.durationOutputTargets.forEach(target => target.innerHTML = (duration / 12) + ' Jahre');
      this.financeCaseOutputTargets.forEach(target => target.innerHTML = this.translateFinanceCase(financeCase));
      this.hiddenInterestRateOutputOnlyNumberTargets.forEach(target => target.value = `${(tableau.InterestRate__c + ageModifier + brokerModifier)}`)

      if (kfw) {
        this.kfwLogoTargets.forEach(target => target.classList.remove('is-hidden'))
      } else {
        this.kfwLogoTargets.forEach(target => target.classList.add('is-hidden'))
      }
      this.disclaimerTargets.forEach(target => target.classList.remove('is-hidden'))
    } else {
      this.interestRateOutputTargets.forEach(target => target.innerHTML = '');
      this.installmentOutputTargets.forEach(target => target.innerHTML = '');
      this.kfwLogoTargets.forEach(target => target.classList.add('is-hidden'))
      this.disclaimerTargets.forEach(target => target.classList.add('is-hidden'))
      this.amountOutputTargets.forEach(target => target.innerHTML = '')
      this.durationOutputTargets.forEach(target => target.innerHTML = '')
      if (this.isInstantLoanValue || instantLoan) {
        if (this.normalizedBrokerRate > 0 ) {
          this.financeCaseOutputTargets.forEach(target => target.innerHTML = 'Sie füllen aus, Ihr Kunde unterschreibt noch heute:<br/>Unser Praxis-DirektKredit.');
        } else {
          this.financeCaseOutputTargets.forEach(target => target.innerHTML = 'Ausfüllen und noch heute unterschreiben:<br/>Unser Praxis-DirektKredit.');
        }
      } else {
        this.financeCaseOutputTargets.forEach(target => target.innerHTML = 'Bitte wählen Sie Ihren Finanzierungswunsch.');
      }
    }

    if (instantLoanableFinanceCase || this.isInstantLoanValue) {
      if (instantLoan || this.isInstantLoanValue) {
        this.vvrbLogoTargets.forEach(target => target.classList.remove('is-hidden'))

        this.instantLoanInfoTargets.forEach(target => {
          let text = '';
          target.innerHTML = text;
          if (this.normalizedBrokerRate > 0 ) {
            this.financeCaseOutputTargets.forEach(target => target.innerHTML = 'Sie füllen aus, Ihr Kunde unterschreibt noch heute:<br/>Unser Praxis-DirektKredit.');
          } else {
            this.financeCaseOutputTargets.forEach(target => target.innerHTML = 'Ausfüllen und noch heute unterschreiben:<br/>Unser Praxis-DirektKredit.');
          }
        })
      } else {
        this.vvrbLogoTargets.forEach(target => target.classList.add('is-hidden'));
        this.instantLoanInfoTargets.forEach(target => {
          let text = 'Bis 100.000€ und max. 5 Jahre Laufzeit auch als schneller Praxis-DirektKredit verfügbar.';
          target.innerHTML = text;
        }
      )}
    } else {
    this.vvrbLogoTargets.forEach(target => target.classList.add('is-hidden'));
    this.instantLoanInfoTargets.forEach(target => {
      target.innerHTML = '';
    }
    )}

    if (this.practiceTypeValue !== null && this.practiceTypeValue !== '') {
      this.practiceTypeOutputTargets.forEach(target => target.innerHTML = this.practiceTypeValue);
    }
    if (this.medicalSpecialisationValue !== null && this.medicalSpecialisationValue !== '') {
      this.medicalSpecialisationOutputTargets.forEach(target => target.innerHTML = this.medicalSpecialisationValue);
    }
  }

  possibleProductTableaus(amount, duration, ratingClass, kfw, instantLoan) {
    return this.allTableaus.filter(tableau => {
      return (
        tableau.PromotionalProgram__c === kfw &&
        tableau.RatingClassPick__c === ratingClass &&
        tableau.DurationFrom__c <= duration &&
        tableau.DurationUntil__c >= duration &&
        tableau.AmountFrom__c <= amount &&
        tableau.AmountTo__c >= amount &&
        (kfw || !(tableau.ProductCode).includes('KFW')) &&
        ((tableau.BankNameHelper__c === 'VVRB') === instantLoan)
      );
    });
  }

  cheapestProduct(tableaus) {
    return tableaus.reduce((cheapest, current) => {
      return current.InterestRate__c < cheapest.InterestRate__c ? current : cheapest;
    });
  }

  calculateMonthlyInstallment(tableau, amount, duration) {
    const ageModifier = this.olderThanFiveYears ? 0.2 : 0;
    const brokerModifier = Math.ceil((this.normalizedBrokerRate / 100) / (duration / 12 / 2) * 20) / 20;
    const monthlyInterestRate = (tableau.InterestRate__c + ageModifier + brokerModifier)/ 100 / 12;
    const numberOfPayments = duration;

    return (amount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
           (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  }

  setOlderThanFiveYearsFalse() {
    this.olderThanFiveYears = false;
  }

  setOlderThanFiveYearsTrue() {
    this.olderThanFiveYears = true;
  }
  translateFinanceCase(financeCase) {
    const translationMap = {
      "New Practice":  "Gründung",
      "Buy Practice": "Übernahme",
      "Existing Practice": "Erweiterung",
      "Equipment": "Geräte und Ausstattung",
      "Liquidity": "Liquidität und Betriebsmittel",
      "Debt Restructuring": "Umschuldung"
    }

    return translationMap[financeCase] ?? ''
  }
}
